// src/components/canine-teeth/ImageSelector.js

import React, { useState, useRef, useEffect, useCallback } from 'react';
import SubImageAnnotator from './SubImageAnnotator';
import CategorySelector from './CategorySelector';
import { FaDownload, FaFileAlt, FaInfoCircle, FaShieldAlt } from 'react-icons/fa';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { saveSession } from '../../indexedDB';
import PropTypes from 'prop-types';
import * as UTIF from 'utif';
import { useNavigate } from 'react-router-dom';

// Import I3M calculation utilities
import { 
  analyzeI3M, 
  formatProbability, 
  getProbabilityColor,
  getGoldenStandardComparison,
  THRESHOLD,
  I3M_RANGE 
} from '../../utils/i3mCalculations';
import { getEthnicityParams } from '../../utils/probabilityTables';



// All'inizio di ImageSelector.js, subito dopo gli import
const toothTypes = [
  "lower third molar R",
  "lower third molar L",
];

function ImageSelector({
  imageFile,
  userEmail,
  tipo = '',
  posizione = '',
  sesso,
  regione,
  realAge = '',
  selectionRequired = true,
  maxTotalPoints = 2,
  colorCategories = ['blue', 'red'],
  categoryColors = { blue: '#2196F3', red: '#F44336' },
  passCategoryToAnnotation = true,
  maxPointsPerCategory = {},
  useLeftRightInNames = false,
  categoriesToAnnotate = ['blue', 'red'],
  loadedSession = null,
  loadedSubImages = [],
  updateSessionsCount,
  userInfo, // Assicurati che userInfo sia passato correttamente
}) {
  // Definizione dei titoli per ciascun punto
  const navigate = useNavigate();
  const pointTitles = [
    "Select RIGHT lower third molar tooth",
    "Select LEFT lower third molar tooth",
  ];
  const [isLoading, setIsLoading] = useState(false);
  const [i3mL, setI3mL] = useState(null);
  const [i3mR, setI3mR] = useState(null);
  const [cutoffI3ML, setCutoffI3ML] = useState(null);
  const [cutoffI3MR, setCutoffI3MR] = useState(null);
  const [agei3mL, setAgeI3mL] = useState(null);
  const [agei3mR, setAgeI3mR] = useState(null);

  // NEW: Advanced probability analysis states
  const [i3mAnalysis, setI3mAnalysis] = useState(null);
  const [pAdultR, setPAdultR] = useState(null);
  const [pAdultL, setPAdultL] = useState(null);
  const [probMedia, setProbMedia] = useState(null);
  const [decisionMedia, setDecisionMedia] = useState(null);
  const [decisionMaxProtection, setDecisionMaxProtection] = useState(null);
  const [goldenStandardComparison, setGoldenStandardComparison] = useState(null);

  const [selectedPoints, setSelectedPoints] = useState([]);
  const [subImages, setSubImages] = useState([]);
  const [currentStep, setCurrentStep] = useState(
    selectionRequired ? 'selection' : 'annotation'
  );
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [loadedImage, setLoadedImage] = useState(null);
  const [category, setCategory] = useState(colorCategories[0]);
  const [mousePosition, setMousePosition] = useState(null);
  const [allAnnotations, setAllAnnotations] = useState({});
  const [annotatedImagesData, setAnnotatedImagesData] = useState({});
  const imageRef = useRef(null);
  const canvasRef = useRef(null);

  const [isDraggingPoint, setIsDraggingPoint] = useState(false);
  const [draggingPointIndex, setDraggingPointIndex] = useState(null);
  const clickDisabled = useRef(false);
  const [imageDataURL, setImageDataURL] = useState(null);
  const imageDataURLRef = useRef(null);

  // Stati per il ridimensionamento del rettangolo
  const [rectangleDimensions, setRectangleDimensions] = useState({});
  const [isDraggingCorner, setIsDraggingCorner] = useState(false);
  const [draggingCornerData, setDraggingCornerData] = useState(null);
  const [hoveredCorner, setHoveredCorner] = useState(null);


  useEffect(() => {
    if (loadedSession) {
      // Extract the original image dataURL from loadedSession
      if (loadedSession.subImage?.dataURL) {
        imageDataURLRef.current = loadedSession.subImage.dataURL;
        setImageDataURL(loadedSession.subImage.dataURL);
        console.log('✓ Original image dataURL extracted from loadedSession');
      }
      
      if (loadedSubImages && loadedSubImages.length > 0) {
        // Process loadedSubImages to ensure proper structure
        const processedSubImages = loadedSubImages.map((session) => ({
          id: session.id || session.subImage.name || Math.random(),
          subImage: session.subImage,
          annotations: session.annotations || {},
          isExcluded: session.isExcluded || false,
        }));

        setSubImages(processedSubImages);

        // Initialize all annotations
        const annotations = {};
        processedSubImages.forEach((subImg) => {
          annotations[subImg.id] = subImg.annotations || {};
        });
        setAllAnnotations(annotations);
        setCurrentStep('annotation');

        // Set excluded images
        const excluded = processedSubImages
          .filter((subImg) => subImg.isExcluded)
          .map((subImg) => subImg.id);
        setExcludedSubImages(excluded);

        // Initialize lineLengths from ratios of annotations
        const initialLineLengths = {};
        processedSubImages.forEach((subImg) => {
          if (subImg.annotations && subImg.annotations.ratios) {
            initialLineLengths[subImg.id] = subImg.annotations.ratios; // Correctly assign ratios
          }
        });
        setLineLengths(initialLineLengths);
      } else {
        // Handle single session loading
        const newSubImages = [{ id: loadedSession.id || 1, subImage: loadedSession }];
        setSubImages(newSubImages);
        setCurrentStep('annotation');
        setAllAnnotations({ [loadedSession.id || 1]: loadedSession.annotations || {} });

        const ratios = loadedSession.annotations?.ratios || {};
        setLineLengths({ [loadedSession.id || 1]: ratios });
      }
    }
  }, [loadedSession, loadedSubImages]);






  useEffect(() => {
    if (!imageFile) return;

    const fileType = imageFile.type;

    if (fileType === 'image/tiff' || fileType === 'image/x-tiff') {
      // Handle TIFF images using UTIF.js
      const reader = new FileReader();
      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        const ifds = UTIF.decode(arrayBuffer);

        // Use UTIF.decodeImage instead of UTIF.decodeImages
        UTIF.decodeImage(arrayBuffer, ifds[0]); // Decode the first image/frame

        const firstPage = ifds[0];
        const rgba = UTIF.toRGBA8(firstPage);

        const canvas = document.createElement('canvas');
        canvas.width = firstPage.width;
        canvas.height = firstPage.height;
        const ctx = canvas.getContext('2d');
        const imageData = ctx.createImageData(canvas.width, canvas.height);
        imageData.data.set(rgba);
        ctx.putImageData(imageData, 0, 0);

        const dataURL = canvas.toDataURL();
        setImageDataURL(dataURL);
        imageDataURLRef.current = dataURL;
        console.log('TIFF image dataURL saved');

        const img = new Image();
        img.onload = () => {
          setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
          setLoadedImage(img);

          if (!selectionRequired && !loadedSession) {
            const nameBase = imageFile.name.replace(/\.[^/.]+$/, '');

            const subImg = {
              subImage: {
                id: 1,
                name: `${nameBase}.jpg`,
                dataURL: dataURL,
                center: { x: img.width / 2, y: img.height / 2 },
                category: passCategoryToAnnotation ? null : undefined,
                originalPosition: { x: 0, y: 0 },
                originalImageName: imageFile.name,
                tool: 'original-image-i3m',
                email: userEmail,
                tipo,
                posizione,
                sesso,
                regione,
                timestamp: new Date().toISOString(),
                isExcluded: false,
              },
            };

            setSubImages([subImg]);
            setCurrentStep('annotation');
          }
        };
        img.src = dataURL;
      };
      reader.readAsArrayBuffer(imageFile);
    } else {
      // Handle other image types (JPEG, PNG, etc.)
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataURL = e.target.result;
        setImageDataURL(dataURL);
        imageDataURLRef.current = dataURL;
        console.log('Regular image dataURL saved');

        const img = new Image();
        img.onload = () => {
          setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
          setLoadedImage(img);

          if (!selectionRequired && !loadedSession) {
            const nameBase = imageFile.name.replace(/\.[^/.]+$/, '');

            const subImg = {
              subImage: {
                id: 1,
                name: `${nameBase}.jpg`,
                dataURL: dataURL,
                center: { x: img.width / 2, y: img.height / 2 },
                category: passCategoryToAnnotation ? null : undefined,
                originalPosition: { x: 0, y: 0 },
                originalImageName: imageFile.name,
                tool: 'original-image-i3m',
                email: userEmail,
                tipo,
                posizione,
                sesso,
                regione,
                timestamp: new Date().toISOString(),
                isExcluded: false,
              },
            };

            setSubImages([subImg]);
            setCurrentStep('annotation');
          }
        };
        img.src = dataURL;
      };
      reader.readAsDataURL(imageFile);
    }
  }, [
    imageFile,
    selectionRequired,
    passCategoryToAnnotation,
    loadedSession,
    userEmail,
    tipo,
    posizione,
    sesso,
    regione,
  ]);


  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
  };

  // Funzione per ottenere le dimensioni del rettangolo per un punto specifico
  const getRectDimensions = useCallback((pointIndex) => {
    return rectangleDimensions[pointIndex] || { width: 250, height: 350 };
  }, [rectangleDimensions]);

  // Funzione per aggiornare le dimensioni del rettangolo
  const updateRectDimensions = useCallback((pointIndex, newDimensions) => {
    setRectangleDimensions(prev => ({
      ...prev,
      [pointIndex]: newDimensions
    }));
  }, []);

  // Funzione per calcolare le coordinate degli angoli del rettangolo
  const getRectCorners = useCallback((point, pointIndex, scaleX, scaleY) => {
    const rect = getRectDimensions(pointIndex);
    const centerX = point.x * scaleX;
    const centerY = point.y * scaleY;
    const rectWidth = rect.width * scaleX;
    const rectHeight = rect.height * scaleY;
    
    const left = centerX - rectWidth / 2;
    const top = centerY - rectHeight / 2;
    const right = left + rectWidth;
    const bottom = top + rectHeight;

    return {
      topLeft: { x: left, y: top },
      topRight: { x: right, y: top },
      bottomLeft: { x: left, y: bottom },
      bottomRight: { x: right, y: bottom },
      center: { x: centerX, y: centerY },
      width: rectWidth,
      height: rectHeight
    };
  }, [getRectDimensions]);

  // Funzione per verificare se il mouse è sopra un angolo
  const getHoveredCorner = useCallback((mouseX, mouseY, corners, pointIndex) => {
    const threshold = 10; // Pixel di tolleranza per il hover
    
    const cornerPositions = [
      { name: 'topLeft', pos: corners.topLeft },
      { name: 'topRight', pos: corners.topRight },
      { name: 'bottomLeft', pos: corners.bottomLeft },
      { name: 'bottomRight', pos: corners.bottomRight }
    ];

    for (let corner of cornerPositions) {
      const dx = mouseX - corner.pos.x;
      const dy = mouseY - corner.pos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance <= threshold) {
        return { pointIndex, corner: corner.name, pos: corner.pos };
      }
    }
    
    return null;
  }, []);

  const handleImageClick = (e) => {
    if (isDraggingPoint || isDraggingCorner) return;
    if (clickDisabled.current) {
      clickDisabled.current = false;
      return;
    }

    const totalPoints = selectedPoints.length;
    if (totalPoints >= maxTotalPoints) {
      alert(`Hai selezionato il numero massimo di ${maxTotalPoints} punti.`);
      return;
    }

    // Assegna automaticamente il colore basato sull'indice del punto
    const pointCategory = colorCategories[totalPoints] || colorCategories[0];

    const pointsInCategory = selectedPoints.filter((p) => p.category === pointCategory)
      .length;
    const categoryMax = maxPointsPerCategory[pointCategory] || maxTotalPoints;

    if (pointsInCategory >= categoryMax) {
      alert(`Hai raggiunto il numero massimo di punti per la categoria ${pointCategory}.`);
      return;
    }

    const img = imageRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    const x = (e.clientX - rect.left);
    const y = (e.clientY - rect.top);
    const proportionX = imageSize.width / img.clientWidth;
    const proportionY = imageSize.height / img.clientHeight;
    const finalX = x * proportionX;
    const finalY = y * proportionY;

    const newPointIndex = selectedPoints.length;
    setSelectedPoints((prevPoints) => [...prevPoints, { x: finalX, y: finalY, category: pointCategory }]);
    
    // Inizializza le dimensioni del rettangolo per il nuovo punto
    updateRectDimensions(newPointIndex, { width: 250, height: 350 });
  };

  const handleMouseDown = (e) => {
    const img = imageRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    const x = (e.clientX - rect.left);
    const y = (e.clientY - rect.top);
    const proportionX = imageSize.width / img.clientWidth;
    const proportionY = imageSize.height / img.clientHeight;
    const finalX = x * proportionX;
    const finalY = y * proportionY;

    // Verifica se stiamo cliccando su un angolo del rettangolo
    const scaleX = img.clientWidth / imageSize.width;
    const scaleY = img.clientHeight / imageSize.height;
    
    for (let i = 0; i < selectedPoints.length; i++) {
      const point = selectedPoints[i];
      if (categoriesToAnnotate.includes(point.category)) {
        const corners = getRectCorners(point, i, scaleX, scaleY);
        const hoveredCorner = getHoveredCorner(x, y, corners, i);
        
        if (hoveredCorner) {
          setIsDraggingCorner(true);
          setDraggingCornerData(hoveredCorner);
          clickDisabled.current = true;
          return;
        }
      }
    }

    // Verifica se stiamo cliccando su un punto esistente per trascinarlo
    selectedPoints.forEach((point, index) => {
      const dx = finalX - point.x;
      const dy = finalY - point.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= 10) {
        setIsDraggingPoint(true);
        setDraggingPointIndex(index);
        setMousePosition(null);
        clickDisabled.current = false;
      }
    });
  };

  const handleMouseMove = (e) => {
    const img = imageRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    const x = (e.clientX - rect.left);
    const y = (e.clientY - rect.top);
    const proportionX = imageSize.width / img.clientWidth;
    const proportionY = imageSize.height / img.clientHeight;
    const finalX = x * proportionX;
    const finalY = y * proportionY;

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Se stiamo trascinando un angolo
    if (isDraggingCorner && draggingCornerData) {
      const { pointIndex, corner } = draggingCornerData;
      const point = selectedPoints[pointIndex];
      const currentRect = getRectDimensions(pointIndex);
      
      // Calcola le nuove dimensioni basate sulla posizione del mouse
      const centerX = point.x;
      const centerY = point.y;
      
      let newWidth = currentRect.width;
      let newHeight = currentRect.height;
      
      switch (corner) {
        case 'topLeft':
          newWidth = Math.abs((centerX - finalX) * 2);
          newHeight = Math.abs((centerY - finalY) * 2);
          break;
        case 'topRight':
          newWidth = Math.abs((finalX - centerX) * 2);
          newHeight = Math.abs((centerY - finalY) * 2);
          break;
        case 'bottomLeft':
          newWidth = Math.abs((centerX - finalX) * 2);
          newHeight = Math.abs((finalY - centerY) * 2);
          break;
        case 'bottomRight':
          newWidth = Math.abs((finalX - centerX) * 2);
          newHeight = Math.abs((finalY - centerY) * 2);
          break;
        default:
          // Mantieni le dimensioni attuali se il corner non è riconosciuto
          break;
      }
      
      // Limita le dimensioni minime e massime
      newWidth = Math.max(50, Math.min(newWidth, imageSize.width));
      newHeight = Math.max(50, Math.min(newHeight, imageSize.height));
      
      updateRectDimensions(pointIndex, { width: newWidth, height: newHeight });
      clickDisabled.current = true;
      return;
    }

    // Se stiamo trascinando un punto
    if (isDraggingPoint && draggingPointIndex !== null) {
      setSelectedPoints((prevPoints) => {
        const newPoints = [...prevPoints];
        newPoints[draggingPointIndex] = { ...newPoints[draggingPointIndex], x: finalX, y: finalY };
        return newPoints;
      });
      clickDisabled.current = true;
      return;
    }

    // Gestione del hover sugli angoli e sui punti
    let isOverPoint = false;
    let isOverCorner = false;
    let cursorStyle = 'crosshair';

    // Verifica hover sui punti
    selectedPoints.forEach((point) => {
      const dx = finalX - point.x;
      const dy = finalY - point.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= 10) {
        isOverPoint = true;
        cursorStyle = 'move';
      }
    });

    // Verifica hover sugli angoli dei rettangoli
    if (!isOverPoint) {
      const scaleX = img.clientWidth / imageSize.width;
      const scaleY = img.clientHeight / imageSize.height;
      
      for (let i = 0; i < selectedPoints.length; i++) {
        const point = selectedPoints[i];
        if (categoriesToAnnotate.includes(point.category)) {
          const corners = getRectCorners(point, i, scaleX, scaleY);
          const hoveredCorner = getHoveredCorner(x, y, corners, i);
          
          if (hoveredCorner) {
            isOverCorner = true;
            setHoveredCorner(hoveredCorner);
            // Cursore diverso per ogni angolo
            switch (hoveredCorner.corner) {
              case 'topLeft':
              case 'bottomRight':
                cursorStyle = 'nw-resize';
                break;
              case 'topRight':
              case 'bottomLeft':
                cursorStyle = 'ne-resize';
                break;
              default:
                cursorStyle = 'crosshair';
                break;
            }
            break;
          }
        }
      }
      
      if (!isOverCorner) {
        setHoveredCorner(null);
      }
    }

    canvas.style.cursor = cursorStyle;

    if (!isDraggingPoint && !isDraggingCorner) {
      setMousePosition({ x: finalX, y: finalY });
    }
  };

  const handleMouseUp = () => {
    setIsDraggingPoint(false);
    setDraggingPointIndex(null);
    setIsDraggingCorner(false);
    setDraggingCornerData(null);
  };

  const handleMouseLeave = () => {
    setMousePosition(null);
    setHoveredCorner(null);
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
    }
  };

  const handleUndoLastPoint = () => {
    if (selectedPoints.length === 0) {
      alert('Nessun punto da annullare.');
      return;
    }
    
    const lastPointIndex = selectedPoints.length - 1;
    setSelectedPoints((prevPoints) => prevPoints.slice(0, -1));
    
    // Rimuovi anche le dimensioni del rettangolo associato
    setRectangleDimensions((prev) => {
      const newDimensions = { ...prev };
      delete newDimensions[lastPointIndex];
      return newDimensions;
    });
  };

  const handleResetPoints = () => {
    setSelectedPoints([]);
    setRectangleDimensions({});
  };

  const handleVerification = () => {
    if (selectedPoints.length >= 1) {
      createSubImages(); // Crea le sotto-immagini direttamente
      setCurrentStep('annotation'); // Passa subito all'annotazione
    } else {
      alert(`Devi selezionare esattamente ${maxTotalPoints} punti.`);
    }
  };

  const createSubImages = async () => {
    setIsLoading(true);
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Save the original image dataURL
      if (loadedImage && !imageDataURL) {
        canvas.width = loadedImage.width;
        canvas.height = loadedImage.height;
        ctx.drawImage(loadedImage, 0, 0);
        const originalDataURL = canvas.toDataURL('image/jpeg', 1);
        setImageDataURL(originalDataURL);
        console.log('Original image dataURL saved:', originalDataURL.substring(0, 50) + '...');
      }

      // Iterate over selectedPoints using their original indices so rect dims and toothType align
      const newSubImages = [];
      for (let i = 0; i < selectedPoints.length; i++) {
        const point = selectedPoints[i];
        if (!categoriesToAnnotate.includes(point.category)) continue;

        // Use custom dimensions for the original index i
        const customDimensions = getRectDimensions(i);
        const actualRectWidth = customDimensions.width;
        const actualRectHeight = customDimensions.height;

        let sx = Math.round(point.x - actualRectWidth / 2);
        let sy = Math.round(point.y - actualRectHeight / 2);
        const sWidth = actualRectWidth;
        const sHeight = actualRectHeight;

        if (sx < 0) sx = 0;
        if (sy < 0) sy = 0;
        if (sx + sWidth > imageSize.width) sx = imageSize.width - sWidth;
        if (sy + sHeight > imageSize.height) sy = imageSize.height - sHeight;

        canvas.width = actualRectWidth;
        canvas.height = actualRectHeight;

        ctx.drawImage(
          loadedImage,
          sx,
          sy,
          sWidth,
          sHeight,
          0,
          0,
          actualRectWidth,
          actualRectHeight
        );

        const dataURL = canvas.toDataURL('image/jpeg', 1);
        const nameBase = imageFile.name.replace(/\.[^/.]+$/, '');

        const toothType = toothTypes[i] || toothTypes[i % toothTypes.length];
        const formattedToothType = toothType.replace(/ /g, '_');
        const subImageName = `${nameBase}_${formattedToothType}_${newSubImages.length + 1}.jpg`;

        newSubImages.push({
          id: newSubImages.length + 1,
          subImage: {
            name: subImageName,
            dataURL,
            center: { x: point.x, y: point.y },
            category: passCategoryToAnnotation ? point.category : undefined,
            originalPosition: { x: sx, y: sy },
            originalImageName: imageFile.name,
            tool: 'i3m',
            email: userEmail,
            tipo,
            posizione,
            sesso,
            regione,
            realAge,
            timestamp: new Date().toISOString(),
            isExcluded: false,
            toothType: toothType,
            customDimensions: { width: actualRectWidth, height: actualRectHeight },
          }
        });
      }

      // Save each sub-image session
      await Promise.all(
        newSubImages.map(async (subImageData) => {
          const sessionData = {
            subImage: subImageData.subImage,
            tool: subImageData.subImage.tool,
            email: subImageData.subImage.email,
            tipo: subImageData.subImage.tipo,
            posizione: subImageData.subImage.posizione,
            sesso: subImageData.subImage.sesso,
            regione: subImageData.subImage.regione,
            realAge: subImageData.subImage.realAge,
            isExcluded: subImageData.subImage.isExcluded,
            timestamp: subImageData.subImage.timestamp,
          };
          await saveSession(sessionData);
        })
      );

      // Save the original image session
      const originalCanvas = document.createElement('canvas');
      const originalCtx = originalCanvas.getContext('2d');

      originalCanvas.width = imageSize.width;
      originalCanvas.height = imageSize.height;

      originalCtx.drawImage(loadedImage, 0, 0);

      const originalDataURL = originalCanvas.toDataURL('image/jpeg', 1);

      const originalSessionData = {
        subImage: {
          name: imageFile.name,
          dataURL: originalDataURL,
          center: { x: 0, y: 0 },
          category: null,
          originalPosition: { x: 0, y: 0 },
          originalImageName: imageFile.name,
        },
        tool: 'original-image-i3m',
        email: userEmail,
        tipo,
        posizione,
        sesso,
        regione,
        realAge,
        timestamp: new Date().toISOString(),
      };

      await saveSession(originalSessionData);

      setSubImages(newSubImages);
      setCurrentStep('annotation');
    } catch (error) {
      console.error('Errore durante il salvataggio delle sessioni:', error);
      alert('Si è verificato un errore durante il salvataggio. Per favore riprova.');
    } finally {
      setIsLoading(false);
    }
  };

  const collectAnnotations = useCallback(
    (subImageId, annotations, annotatedImageDataURL) => {
      setAllAnnotations((prevAnnotations) => ({
        ...prevAnnotations,
        [subImageId]: annotations,
      }));

      if (annotatedImageDataURL) {
        setAnnotatedImagesData((prevData) => ({
          ...prevData,
          [subImageId]: annotatedImageDataURL,
        }));
      }
    },
    [] // Dependencies can be empty because setState functions are stable
  );


  const handleDownloadAnnotationsJSON = () => {
    if (subImages.length === 0) {
      alert('Nothing to download.');
      return;
    }

    const annotationsArray = subImages.map((subImage) => {
      const subImageAnnotations = allAnnotations[subImage.id] || {};
      const customDimensions = subImage.subImage.customDimensions || { width: 250, height: 350 };
      return {
        imageName: subImage.subImage.name || '',
        annotations: subImageAnnotations.coordinates || {},
        ratios: subImageAnnotations.ratios || {},
        areas: subImageAnnotations.areas || {},
        estimatedAge: subImageAnnotations.estimatedAge || null,
        email: userEmail || '',
        sesso: sesso || '',
        region: regione || '',
        realAge: realAge || '',
        centerPoint: subImage.subImage.center || {},
        originalPosition: subImage.subImage.originalPosition || {},
        timestamp: subImageAnnotations.timestamp || '',
        subImageDimensions: customDimensions,
        isExcluded: excludedSubImages.includes(subImage.id),
        tool: 'i3m',
        toothType: subImage.subImage.toothType || '',
        category: subImage.subImage.category || '',
        originalImageName: subImage.subImage.originalImageName || '',
        // Informazioni aggiuntive sull'immagine originale
        originalImageDimensions: {
          width: imageSize.width,
          height: imageSize.height
        },
        // Informazioni sul rettangolo di selezione
        selectionRectangle: {
          centerX: subImage.subImage.center?.x || 0,
          centerY: subImage.subImage.center?.y || 0,
          width: customDimensions.width,
          height: customDimensions.height,
          topLeft: {
            x: (subImage.subImage.center?.x || 0) - customDimensions.width / 2,
            y: (subImage.subImage.center?.y || 0) - customDimensions.height / 2
          },
          bottomRight: {
            x: (subImage.subImage.center?.x || 0) + customDimensions.width / 2,
            y: (subImage.subImage.center?.y || 0) + customDimensions.height / 2
          }
        },
        // Metadati di creazione
        creationMetadata: {
          createdAt: subImage.subImage.timestamp || new Date().toISOString(),
          userEmail: userEmail || '',
          toolVersion: 'i3m-v2.0',
          selectionMethod: 'manual-resize'
        }
      };
    });

    const data = {
      originalImageName: imageFile
        ? imageFile.name
        : loadedSession?.subImage?.originalImageName || '',
      originalImageDimensions: {
        width: imageSize.width,
        height: imageSize.height
      },
      // Informazioni sui punti selezionati originali
      selectedPoints: selectedPoints.map((point, index) => ({
        index: index,
        coordinates: { x: point.x, y: point.y },
        category: point.category,
        toothType: toothTypes[index] || '',
        rectangleDimensions: getRectDimensions(index),
        pointTitle: pointTitles[index] || ''
      })),
      // Configurazione del tool
      toolConfiguration: {
        maxTotalPoints: maxTotalPoints,
        colorCategories: colorCategories,
        categoryColors: categoryColors,
        categoriesToAnnotate: categoriesToAnnotate
      },
      // Informazioni della sessione
      sessionInfo: {
        userEmail: userEmail || '',
        sesso: sesso || '',
        regione: regione || '',
        realAge: realAge || '',
        tipo: tipo || '',
        posizione: posizione || '',
        exportDate: new Date().toISOString(),
        tool: 'i3m'
      },
      annotations: annotationsArray,
      // Risultati calcolati
      results: {
        i3mL: i3mL,
        i3mR: i3mR,
        cutoffI3ML: cutoffI3ML,
        cutoffI3MR: cutoffI3MR,
        agei3mL: agei3mL,
        agei3mR: agei3mR,
        realAge: realAge || ''
      }
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    saveAs(blob, 'annotations.json');
  };


  // Aggiungi queste costanti per mappare i colori delle linee
  const lineKeyToColor = {
    line1: 'blue',
    line2: 'red',
    line3: 'green',
  };

  const getLineColor = (lineKey) => {
    return lineKeyToColor[lineKey] || '#f0f0f0';
  };

  const lineThickness = 1;
  const pointRadius = 2;

  const handleGenerateReport = async () => {
    if (subImages.length === 0) {
      alert('No annotations available. Please complete the annotation process first.');
      return;
    }

    // Generate annotated images for all sub-images
    console.log('Generating annotated images for report...');
    const generatedAnnotatedImages = { ...annotatedImagesData };
    
    // Create promises for all image generations
    const imageGenerationPromises = subImages.map((subImage) => {
      // Skip if already have annotated image
      if (generatedAnnotatedImages[subImage.id]) {
        return Promise.resolve();
      }
      
      return new Promise((resolve) => {
        try {
          if (subImage.subImage?.dataURL) {
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              canvas.width = img.width;
              canvas.height = img.height;
              ctx.drawImage(img, 0, 0);
              
              // Draw annotations on the canvas if available
              const annotations = allAnnotations[subImage.id];
              if (annotations && annotations.coordinates) {
                const coordinates = annotations.coordinates;
                const colorMap = {
                  'line1': 'blue',
                  'line2': 'red',
                  'line3': 'green'
                };
                
                Object.entries(coordinates).forEach(([lineKey, points]) => {
                  const color = colorMap[lineKey] || 'blue';
                  
                  if (Array.isArray(points) && points.length === 2) {
                    // Set line color and style
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 3;
                    ctx.beginPath();
                    ctx.moveTo(points[0].x, points[0].y);
                    ctx.lineTo(points[1].x, points[1].y);
                    ctx.stroke();
                    
                    // Draw points
                    ctx.fillStyle = color;
                    points.forEach(point => {
                      ctx.beginPath();
                      ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI);
                      ctx.fill();
                    });
                  }
                });
              }
              
              generatedAnnotatedImages[subImage.id] = canvas.toDataURL('image/png');
              console.log(`✓ Generated annotated image for sub-image ${subImage.id}`);
              resolve();
            };
            img.onerror = () => {
              console.error(`Error loading image for sub-image ${subImage.id}`);
              resolve();
            };
            img.src = subImage.subImage.dataURL;
          } else {
            resolve();
          }
        } catch (error) {
          console.error(`Error generating annotated image for sub-image ${subImage.id}:`, error);
          resolve();
        }
      });
    });
    
    // Wait for all image generations to complete
    await Promise.all(imageGenerationPromises);

    // Strategy to get original image
    let originalDataURL = imageDataURLRef.current;
    let originalImageName = imageFile?.name || 'image';
    
    // Priority 1: Look for subImage with tool === 'original-image-i3m'
    if (!originalDataURL && subImages.length > 0) {
      const originalSubImage = subImages.find(s => s.subImage?.tool === 'original-image-i3m');
      if (originalSubImage?.subImage?.dataURL) {
        originalDataURL = originalSubImage.subImage.dataURL;
        originalImageName = originalSubImage.subImage.name || originalImageName;
        console.log('✓ Found original image with tool=original-image-i3m');
      }
    }
    
    // Priority 2: Generate from loadedImage if available
    if (!originalDataURL && loadedImage) {
      const canvas = document.createElement('canvas');
      canvas.width = loadedImage.width;
      canvas.height = loadedImage.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(loadedImage, 0, 0);
      originalDataURL = canvas.toDataURL('image/jpeg', 1);
      console.log('✓ Original image dataURL generated from loadedImage');
    }

    // Build probability results object from current state
    // Parse i3m values as numbers to ensure correct comparisons
    const i3mRNum = typeof i3mR === 'string' ? parseFloat(i3mR) : i3mR;
    const i3mLNum = typeof i3mL === 'string' ? parseFloat(i3mL) : i3mL;
    
    const currentProbabilityResults = {
      right: pAdultR !== null && pAdultR !== undefined ? {
        i3m: i3mRNum,
        pAdult: pAdultR,
        pMinor: 1 - pAdultR,
        cutoff: cutoffI3MR,
        decision: agei3mR,
        interpolated: i3mRNum !== null && i3mRNum > 0.20  // Flag if value was extrapolated
      } : null,
      left: pAdultL !== null && pAdultL !== undefined ? {
        i3m: i3mLNum,
        pAdult: pAdultL,
        pMinor: 1 - pAdultL,
        cutoff: cutoffI3ML,
        decision: agei3mL,
        interpolated: i3mLNum !== null && i3mLNum > 0.20  // Flag if value was extrapolated
      } : null
    };
    
    // Debug log to verify probability data
    console.log('Building probabilityResults:', {
      pAdultR, pAdultL, i3mRNum, i3mLNum,
      currentProbabilityResults
    });

    // Build combined results object from current state
    const currentCombinedResults = {
      probMedia: probMedia,
      decisionMedia: decisionMedia,
      decisionMaxProtection: decisionMaxProtection,
      goldenStandardComparison: goldenStandardComparison
    };
    
    console.log('Building combinedResults:', currentCombinedResults);

    // Prepare data to pass to ReportGenerator
    const reportData = {
      subImages: subImages,
      annotatedImagesData: generatedAnnotatedImages, // Pass generated annotated images
      imageFile: {
        name: originalImageName,
        dataURL: originalDataURL,
      },
      userEmail: userEmail,
      tipo: tipo,
      posizione: posizione,
      sesso: sesso,
      regione: regione,
      realAge: realAge,
      userInfo: userInfo,
      allAnnotations: allAnnotations,
      selectedPoints: selectedPoints,
      i3mL: i3mL, // Numeric I3M values
      i3mR: i3mR,
      agei3mL: agei3mL,
      agei3mR: agei3mR,
      cutoffI3ML: cutoffI3ML,
      cutoffI3MR: cutoffI3MR,
      // New probability calculation data
      probabilityResults: currentProbabilityResults,
      combinedResults: currentCombinedResults,
    };

    console.log('Navigating to report with imageFile.dataURL present:', !!reportData.imageFile.dataURL);
    console.log('Generated annotated images:', Object.keys(generatedAnnotatedImages).length);
    // Navigate to ReportGenerator with state
    navigate('/report-generator-i3m', { state: reportData });
  };

  const handleDownloadAnnotatedImages = () => {
    if (subImages.length === 0) {
      alert('Non ci sono immagini annotate da scaricare.');
      return;
    }

    const zip = new JSZip();
    const imgFolder = zip.folder('annotated_images');

    const promises = subImages.map(async (subImage) => {
      let annotatedDataURL = annotatedImagesData[subImage.id];
      if (!annotatedDataURL) {
        // Genera l'immagine annotata
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Usa le dimensioni personalizzate della sotto-immagine
        const customDimensions = subImage.subImage.customDimensions || { width: 250, height: 350 };
        canvas.width = customDimensions.width;
        canvas.height = customDimensions.height;

        // Carica l'immagine della sotto-immagine
        const img = new Image();
        img.src = subImage.subImage.dataURL;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        // Disegna l'immagine sul canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Ottieni le annotazioni
        const annotations = allAnnotations[subImage.id];

        // Disegna le annotazioni sul canvas
        if (annotations && annotations.coordinates) {
          const coordinates = annotations.coordinates;
          Object.entries(coordinates).forEach(([lineKey, points]) => {
            // Ottieni il colore basato su lineKey
            const color = getLineColor(lineKey);
            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = lineThickness;

            if (points.length > 0) {
              ctx.beginPath();
              ctx.moveTo(points[0].x, points[0].y);
              for (let i = 1; i < points.length; i++) {
                ctx.lineTo(points[i].x, points[i].y);
              }
              ctx.stroke();

              points.forEach((point) => {
                ctx.beginPath();
                ctx.arc(point.x, point.y, pointRadius, 0, 2 * Math.PI);
                ctx.fill();
              });
            }
          });
        }

        // Ottieni il data URL dell'immagine annotata
        annotatedDataURL = canvas.toDataURL('image/jpeg', 1);
        // Salva l'immagine annotata nei dati
        setAnnotatedImagesData((prevData) => ({
          ...prevData,
          [subImage.id]: annotatedDataURL,
        }));
      }

      // Aggiungi l'immagine annotata allo ZIP
      return fetch(annotatedDataURL)
        .then((res) => res.blob())
        .then((blob) => {
          imgFolder.file(subImage.subImage.name, blob, { binary: true });
        });
    });


    // Includi l'immagine originale nello ZIP
    if (imageFile) {
      zip.file(imageFile.name, imageFile);
    } else if (loadedSession && loadedSession.subImage && loadedSession.subImage.dataURL) {
      // Se non hai accesso a imageFile, usa l'immagine dal loadedSession
      const imgDataURL = loadedSession.subImage.dataURL;
      const imgName = loadedSession.subImage.name || 'original_image.jpg';
      const imgBlob = dataURLtoBlob(imgDataURL);
      zip.file(imgName, imgBlob, { binary: true });
    }

    Promise.all(promises)
      .then(() => {
        zip.generateAsync({ type: 'blob' }).then((content) => {
          saveAs(content, 'annotated_images.zip');
        });
      })
      .catch((err) => {
        console.error('Errore nel creare lo ZIP:', err);
        alert("C'è stato un errore nel creare il file ZIP.");
      });
  };

  // Funzione per convertire dataURL in Blob
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;

    if (canvas && img) {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const imgWidth = img.clientWidth;
      const imgHeight = img.clientHeight;

  // Set canvas internal pixel buffer to match displayed image size
  canvas.width = imgWidth;
  canvas.height = imgHeight;

  // Ensure the canvas element is positioned and sized exactly over the image
  canvas.style.position = 'absolute';
  canvas.style.left = `${img.offsetLeft}px`;
  canvas.style.top = `${img.offsetTop}px`;
  canvas.style.width = `${imgWidth}px`;
  canvas.style.height = `${imgHeight}px`;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Disegna l'immagine senza zoom
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const scaleX = imgWidth / imageSize.width;
      const scaleY = imgHeight / imageSize.height;

      selectedPoints.forEach((point, pointIndex) => {
        // Disegna il punto centrale
        ctx.fillStyle = categoryColors[point.category] || 'black';
        ctx.beginPath();
        ctx.arc(point.x * scaleX, point.y * scaleY, 5, 0, 2 * Math.PI);
        ctx.fill();

        if (categoriesToAnnotate.includes(point.category)) {
          const rectDimensions = getRectDimensions(pointIndex);
          const rectWidth = rectDimensions.width * scaleX;
          const rectHeight = rectDimensions.height * scaleY;
          
          // Disegna il rettangolo di selezione
          ctx.strokeStyle = categoryColors[point.category] || 'black';
          ctx.lineWidth = 2;
          ctx.setLineDash([10, 5]);
          ctx.strokeRect(
            (point.x - rectDimensions.width / 2) * scaleX,
            (point.y - rectDimensions.height / 2) * scaleY,
            rectWidth,
            rectHeight
          );
          ctx.setLineDash([]);

          // Disegna i punti di controllo degli angoli
          const corners = getRectCorners(point, pointIndex, scaleX, scaleY);
          const cornerSize = 8;
          
          ctx.fillStyle = 'white';
          ctx.strokeStyle = categoryColors[point.category] || 'black';
          ctx.lineWidth = 2;

          // Disegna i quadratini negli angoli
          [corners.topLeft, corners.topRight, corners.bottomLeft, corners.bottomRight].forEach((corner, cornerIndex) => {
            ctx.fillRect(corner.x - cornerSize/2, corner.y - cornerSize/2, cornerSize, cornerSize);
            ctx.strokeRect(corner.x - cornerSize/2, corner.y - cornerSize/2, cornerSize, cornerSize);
          });

          // Evidenzia l'angolo su cui si sta facendo hover
          if (hoveredCorner && hoveredCorner.pointIndex === pointIndex) {
            ctx.fillStyle = categoryColors[point.category] || 'black';
            const hoveredPos = corners[hoveredCorner.corner];
            ctx.fillRect(hoveredPos.x - cornerSize/2, hoveredPos.y - cornerSize/2, cornerSize, cornerSize);
          }
        }
      });

      // Mostra il rettangolo di anteprima quando si muove il mouse
      if (
        !isDraggingPoint &&
        !isDraggingCorner &&
        mousePosition &&
        selectedPoints.length < maxTotalPoints
      ) {
        const { x, y } = mousePosition;
        // Usa il colore per il prossimo punto che verrà selezionato
        const nextPointCategory = colorCategories[selectedPoints.length] || colorCategories[0];
        ctx.strokeStyle = categoryColors[nextPointCategory] || 'black';
        ctx.lineWidth = 2;
        ctx.setLineDash([10, 5]);
        const rectWidth = 250 * scaleX;
        const rectHeight = 350 * scaleY;
        ctx.strokeRect(
          (x - 125) * scaleX,
          (y - 175) * scaleY,
          rectWidth,
          rectHeight
        );
        ctx.setLineDash([]);
      }
    }
  }, [
    selectedPoints,
    mousePosition,
    category,
    categoryColors,
    categoriesToAnnotate,
    imageSize,
    isDraggingPoint,
    isDraggingCorner,
    rectangleDimensions,
    hoveredCorner,
    getRectDimensions,
    getRectCorners,
    colorCategories,
    maxTotalPoints,
  ]);

  // Stati per la gestione delle esclusioni e delle lunghezze delle linee
  const [excludedSubImages, setExcludedSubImages] = useState([]);
  const [lineLengths, setLineLengths] = useState({});

  // Memorizza handleExcludeChange con useCallback
  const handleExcludeChange = useCallback((subImageId, isExcluded) => {
    setExcludedSubImages((prev) => {
      if (isExcluded) {
        // Aggiungi solo se l'ID non è già presente
        if (!prev.includes(subImageId)) {
          return [...prev, subImageId];
        }
        return prev;
      } else {
        // Rimuovi l'ID se presente
        return prev.filter((id) => id !== subImageId);
      }
    });
  }, []);

  const handleRatiosChange = useCallback((subImageId, ratios) => {
    setLineLengths((prev) => {
      const prevRatios = prev[subImageId] || {};
      if (JSON.stringify(prevRatios) === JSON.stringify(ratios)) {
        return prev; // No change
      }
      return {
        ...prev,
        [subImageId]: ratios,
      };
    });
  }, []);



  useEffect(() => {
  const cutoffData = {
      Africa: {
        I3ML: { cutoff: 0.10, Se: 81.20, Sp: 86.90 },
        I3MR: { cutoff: 0.10, Se: 81.30, Sp: 84.60 },
      },
      America: {
        I3ML: { cutoff: 0.10, Se: 80.80, Sp: 87.60 },
        I3MR: { cutoff: 0.09, Se: 80.20, Sp: 89.20 },
      },
      Asia: {
        I3ML: { cutoff: 0.15, Se: 72.80, Sp: 84.10 },
        I3MR: { cutoff: 0.17, Se: 75.60, Sp: 81.00 },
      },
      Europe: {
        I3ML: { cutoff: 0.09, Se: 79.00, Sp: 93.00 },
        I3MR: { cutoff: 0.09, Se: 80.90, Sp: 93.60 },
      },
    };

    let newI3mL = null;
    let newI3mR = null;
    let newCutoffI3ML = null;
    let newCutoffI3MR = null;
    let newAgeI3mL = null;
    let newAgeI3mR = null;
    let calculatedI3mLValue = null;
    let calculatedI3mRValue = null;

  // If no subImages or no region provided, still compute using standard cutoff 0.08
  if (subImages.length === 0) return;

    subImages.forEach((subImg) => {
      const toothType = subImg.subImage.toothType;
      const ratios = lineLengths[subImg.id] || {};
      const line1 = ratios.line1 || 0;
      const line2 = ratios.line2 || 0;
      const line3 = ratios.line3 || 0;

      let i3m = 0;
      if (line1 > 0) {
        i3m = (line2 + line3) / line1;
      }

      // Use provided region cutoff if available, otherwise fallback to standard 0.08
      const standardCutoff = 0.08;
      if (toothType.includes('L')) {
        const cutoffI3MLValue = (regione && cutoffData[regione]?.I3ML?.cutoff) || standardCutoff;
        newI3mL = i3m.toFixed(2);
        newCutoffI3ML = cutoffI3MLValue.toFixed(2);
        newAgeI3mL = i3m < cutoffI3MLValue ? 'Over 18' : 'Under 18';
        calculatedI3mLValue = i3m;
      } else if (toothType.includes('R')) {
        const cutoffI3MRValue = (regione && cutoffData[regione]?.I3MR?.cutoff) || standardCutoff;
        newI3mR = i3m.toFixed(2);
        newCutoffI3MR = cutoffI3MRValue.toFixed(2);
        newAgeI3mR = i3m < cutoffI3MRValue ? 'Over 18' : 'Under 18';
        calculatedI3mRValue = i3m;
      }
    });

    // Update states if values have changed
    if (i3mL !== newI3mL) setI3mL(newI3mL);
    if (i3mR !== newI3mR) setI3mR(newI3mR);
    if (cutoffI3ML !== newCutoffI3ML) setCutoffI3ML(newCutoffI3ML);
    if (cutoffI3MR !== newCutoffI3MR) setCutoffI3MR(newCutoffI3MR);
    if (agei3mL !== newAgeI3mL) setAgeI3mL(newAgeI3mL);
    if (agei3mR !== newAgeI3mR) setAgeI3mR(newAgeI3mR);

    // NEW: Calculate advanced probability analysis
    if (calculatedI3mLValue !== null || calculatedI3mRValue !== null) {
      const analysisRegion = regione || 'Europe';
      
      // Perform full analysis using new calculation module
      const analysis = analyzeI3M({
        i3r: calculatedI3mRValue,
        i3l: calculatedI3mLValue,
        region: analysisRegion,
        gender: sesso,
      });
      
      if (analysis && analysis.results) {
        setI3mAnalysis(analysis);
        setPAdultR(analysis.results.p1);
        setPAdultL(analysis.results.p2);
        setProbMedia(analysis.results.probMedia);
        setDecisionMedia(analysis.results.decisionMedia);
        setDecisionMaxProtection(analysis.results.decisionMaxProtection);
        
        // Get Golden Standard comparison
        const gsComparison = analysis.goldenStandardComparison;
        setGoldenStandardComparison(gsComparison);
      }
    }
  }, [lineLengths, regione, subImages, i3mL, i3mR, cutoffI3ML, cutoffI3MR, agei3mL, agei3mR, sesso]);




  if (isLoading) {
    return (
      <div className="loading-overlay">
        <div className="loading-spinner"></div>
        <p>Uploading... Please Wait</p>
      </div>
    );
  }

  // Rendering condizionale in base allo stato
  if (currentStep === 'selection') {
    if (imageSize.width === 0 || imageSize.height === 0) {
      return <div className="loading-message">Loading image</div>;
    }


    return (
      <div className="selection-container">
        <div>
          <h2 className="step-title">
            {selectedPoints.length < maxTotalPoints
              ? pointTitles[selectedPoints.length]
              : "All points selected"}
          </h2>

          <div className="selected-points-container">
            <p className="points-count">
              Selected points: {selectedPoints.length} (Max {maxTotalPoints})
            </p>
          </div>
          <div className="buttons-container">
            <button
              onClick={handleUndoLastPoint}
              className="undo-button"
              title="Annulla Ultimo Punto"
            >
              Undo last selection
            </button>
            <button onClick={handleResetPoints} className="reset-button">
              Reset Points
            </button>
            {selectedPoints.length > 0 && selectedPoints.length <= maxTotalPoints && (
              <button onClick={handleVerification} className="verify-button">
                Continue
              </button>
            )}
          </div>

          <div className="image-wrapper">
            <img
              src={loadedImage ? loadedImage.src : ''}
              alt="Uploaded"
              ref={imageRef}
              className="main-image"
            />
            <canvas
              ref={canvasRef}
              className="main-canvas"
              onClick={handleImageClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
            />
          </div>
          <CategorySelector
            onChange={handleCategoryChange}
            currentCategory={category}
            categories={colorCategories}
            categoryColors={categoryColors}
            style={{ display: 'none' }}
          />

        </div>
      </div>
    );
  }

  if (currentStep === 'annotation' && subImages.length > 0) {
    return (
      <div>
        {subImages.map((subImg) => (
          <SubImageAnnotator
            key={subImg.id}
            subImage={subImg.subImage}
            subImageId={subImg.id}
            toothType={subImg.subImage.toothType}
            collectAnnotations={collectAnnotations}
            email={userEmail}
            tipo={tipo}
            posizione={posizione}
            sesso={sesso}
            regione={regione}
            realAge={realAge}
            initialAnnotations={allAnnotations[subImg.id]}
            updateSessionsCount={updateSessionsCount}
            onExcludeChange={handleExcludeChange}
            onRatiosChange={handleRatiosChange}
            isExcluded={excludedSubImages.includes(subImg.id)}
          />
        ))}

        <div className="results-footer">
          {/* Sezione Risultati */}
          <div className="results-grid">
            {/* Colonna Sinistra - I3MR */}
            <div className="result-card">
              <div className="result-header">
                <h4>🦷 Right Third Molar (48)</h4>
              </div>
              <div className="result-values">
                <div className="value-item">
                  <span className="value-number" style={i3mR > I3M_RANGE.MAX ? { color: '#f44336' } : {}}>
                    {i3mR}
                  </span>
                  <span className="value-label">I₃M Index</span>
                  {i3mR > I3M_RANGE.MAX && (
                    <span style={{ 
                      fontSize: '0.7em', 
                      color: '#f44336', 
                      display: 'block',
                      marginTop: '2px'
                    }}>
                      ⚠️ Out of range (&gt;0.20)
                    </span>
                  )}
                </div>
                <div className="value-divider">|</div>
                <div className="value-item">
                  <span className="value-number">{cutoffI3MR}</span>
                  <span className="value-label">Regional Threshold</span>
                </div>
              </div>
              {/* Probability display */}
              {pAdultR !== null && (
                <div className="probability-display" style={{ 
                  backgroundColor: getProbabilityColor(pAdultR) === 'green' ? 'rgba(76, 175, 80, 0.2)' : 
                                   getProbabilityColor(pAdultR) === 'red' ? 'rgba(244, 67, 54, 0.2)' : 
                                   'rgba(255, 152, 0, 0.2)',
                  padding: '8px',
                  borderRadius: '4px',
                  marginTop: '8px'
                }}>
                  <span className="probability-value" style={{ 
                    fontSize: '1.2em', 
                    fontWeight: 'bold',
                    color: getProbabilityColor(pAdultR)
                  }}>
                    {formatProbability(pAdultR)}
                  </span>
                  <span className="probability-label" style={{ fontSize: '0.85em', display: 'block' }}>
                    Probability of being Adult
                  </span>
                </div>
              )}
              <div className="estimated-result">
                <span className="estimated-age-value">{agei3mR}</span>
                <span className="estimated-age-label">Classification (Threshold Method)</span>
              </div>
              <div className="meta-info">
                <span className="region-info">📍 {regione}</span>
                {realAge && <span className="real-age-info">📅 Known Age: {realAge}</span>}
              </div>
            </div>

            {/* Colonna Destra - I3ML */}
            <div className="result-card">
              <div className="result-header">
                <h4>🦷 Left Third Molar (38)</h4>
              </div>
              <div className="result-values">
                <div className="value-item">
                  <span className="value-number" style={i3mL > I3M_RANGE.MAX ? { color: '#f44336' } : {}}>
                    {i3mL}
                  </span>
                  <span className="value-label">I₃M Index</span>
                  {i3mL > I3M_RANGE.MAX && (
                    <span style={{ 
                      fontSize: '0.7em', 
                      color: '#f44336', 
                      display: 'block',
                      marginTop: '2px'
                    }}>
                      ⚠️ Out of range (&gt;0.20)
                    </span>
                  )}
                </div>
                <div className="value-divider">|</div>
                <div className="value-item">
                  <span className="value-number">{cutoffI3ML}</span>
                  <span className="value-label">Regional Threshold</span>
                </div>
              </div>
              {/* Probability display */}
              {pAdultL !== null && (
                <div className="probability-display" style={{ 
                  backgroundColor: getProbabilityColor(pAdultL) === 'green' ? 'rgba(76, 175, 80, 0.2)' : 
                                   getProbabilityColor(pAdultL) === 'red' ? 'rgba(244, 67, 54, 0.2)' : 
                                   'rgba(255, 152, 0, 0.2)',
                  padding: '8px',
                  borderRadius: '4px',
                  marginTop: '8px'
                }}>
                  <span className="probability-value" style={{ 
                    fontSize: '1.2em', 
                    fontWeight: 'bold',
                    color: getProbabilityColor(pAdultL)
                  }}>
                    {formatProbability(pAdultL)}
                  </span>
                  <span className="probability-label" style={{ fontSize: '0.85em', display: 'block' }}>
                    Probability of being Adult
                  </span>
                </div>
              )}
              <div className="estimated-result">
                <span className="estimated-age-value">{agei3mL}</span>
                <span className="estimated-age-label">Classification (Threshold Method)</span>
              </div>
              <div className="meta-info">
                <span className="region-info">📍 {regione}</span>
                {realAge && <span className="real-age-info">📅 Known Age: {realAge}</span>}
              </div>
            </div>
          </div>

          {/* Combined Analysis Section */}
          {probMedia !== null && (
            <div className="combined-analysis-section" style={{
              marginTop: '20px',
              padding: '15px',
              backgroundColor: '#2d2d2d',
              borderRadius: '8px',
              border: '1px solid #4CAF50'
            }}>
              <h4 style={{ marginBottom: '15px', color: '#4CAF50' }}>
                <FaInfoCircle style={{ marginRight: '8px' }} />
                📊 Combined Analysis (Both Teeth)
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                {/* Average Probability */}
                <div style={{
                  padding: '12px',
                  backgroundColor: getProbabilityColor(probMedia) === 'green' ? 'rgba(76, 175, 80, 0.15)' : 
                                   getProbabilityColor(probMedia) === 'red' ? 'rgba(244, 67, 54, 0.15)' : 
                                   'rgba(255, 152, 0, 0.15)',
                  borderRadius: '6px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.85em', color: '#b0bec5', marginBottom: '4px' }}>
                    📈 Average Probability of being Adult
                  </div>
                  <div style={{ 
                    fontSize: '1.5em', 
                    fontWeight: 'bold',
                    color: getProbabilityColor(probMedia)
                  }}>
                    {formatProbability(probMedia)}
                  </div>
                  <div style={{ 
                    fontSize: '1em', 
                    marginTop: '8px',
                    padding: '4px 8px',
                    backgroundColor: decisionMedia === 'Adult' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 152, 0, 0.3)',
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}>
                    Final Classification: <strong>{decisionMedia === 'Adult' ? '≥18 years (Adult)' : '<18 years (Minor)'}</strong>
                  </div>
                </div>

                {/* Max Protection Decision */}
                <div style={{
                  padding: '12px',
                  backgroundColor: decisionMaxProtection === 'Adult' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(255, 193, 7, 0.15)',
                  borderRadius: '6px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.85em', color: '#b0bec5', marginBottom: '4px' }}>
                    <FaShieldAlt style={{ marginRight: '4px' }} />
                    🛡️ Precautionary Principle
                  </div>
                  <div style={{ fontSize: '0.75em', color: '#888', marginBottom: '4px', fontStyle: 'italic' }}>
                    (Protects minors in case of doubt)
                  </div>
                  <div style={{ 
                    fontSize: '1.3em', 
                    fontWeight: 'bold',
                    color: decisionMaxProtection === 'Adult' ? '#4CAF50' : '#FFC107',
                    marginTop: '8px'
                  }}>
                    {decisionMaxProtection === 'Adult' ? '≥18 years (Adult)' : '<18 years (Minor)'}
                  </div>
                  <div style={{ 
                    fontSize: '0.8em', 
                    color: '#999',
                    marginTop: '8px'
                  }}>
                    {decisionMedia !== decisionMaxProtection ? (
                      <span style={{ color: '#FFC107' }}>
                        ⚠️ More conservative than average
                      </span>
                    ) : (
                      <span style={{ color: '#4CAF50' }}>
                        ✓ Same as average classification
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Golden Standard Comparison */}
              {goldenStandardComparison && (
                <div style={{
                  marginTop: '15px',
                  padding: '10px',
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  borderRadius: '6px',
                  borderLeft: '3px solid #2196F3'
                }}>
                  <div style={{ fontSize: '0.85em', color: '#2196F3', marginBottom: '8px' }}>
                    🌍 Reference Comparison (European Standard Parameters)
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                      <span style={{ color: '#b0bec5' }}>Reference P(Adult): </span>
                      <strong>{formatProbability(goldenStandardComparison.goldenStandard.probMedia)}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#b0bec5' }}>Reference Classification: </span>
                      <strong>{goldenStandardComparison.goldenStandard.decisionMedia === 'Adult' ? '≥18 years' : '<18 years'}</strong>
                    </div>
                    <div>
                      {goldenStandardComparison.comparison.agreement ? (
                        <span style={{ color: '#4CAF50' }}>✓ Regional & Reference agree</span>
                      ) : (
                        <span style={{ color: '#FF9800' }}>⚠️ Differ by {goldenStandardComparison.comparison.probDifferencePercent}%</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sezione Azioni */}
          <div className="actions-section">
            <button onClick={handleDownloadAnnotatedImages} className="action-button primary">
              <FaDownload className="button-icon" />
              <span>Download Images</span>
            </button>
            <button onClick={handleDownloadAnnotationsJSON} className="action-button secondary">
              <FaDownload className="button-icon" />
              <span>Download Annotations</span>
            </button>
            <button onClick={handleGenerateReport} className="action-button tertiary">
              <FaFileAlt className="button-icon" />
              <span>Generate Report</span>
            </button>
          </div>

        </div>
        
        <div className="bibliography-section">
          <h4>Bibliography</h4>
          <p className="bibliography-text">
            Angelakopoulos N., De Luca S., Oliveira‑Santos I. et al.<br/>
            "Third molar maturity index (I₃M) assessment according to different geographical zones: a large multi‑ethnic study sample"<br/>
            <em>International Journal of Legal Medicine</em> (2023, pubblicato dicembre 2022)
          </p>
        </div>
      </div>
    );
  }

  return null;
}

ImageSelector.propTypes = {
  imageFile: PropTypes.object,
  userEmail: PropTypes.string,
  tipo: PropTypes.string,
  posizione: PropTypes.string,
  sesso: PropTypes.string,
  regione: PropTypes.string,
  realAge: PropTypes.string,
  selectionRequired: PropTypes.bool,
  maxTotalPoints: PropTypes.number,
  colorCategories: PropTypes.arrayOf(PropTypes.string),
  categoryColors: PropTypes.object,
  passCategoryToAnnotation: PropTypes.bool,
  maxPointsPerCategory: PropTypes.object,
  useLeftRightInNames: PropTypes.bool,
  categoriesToAnnotate: PropTypes.arrayOf(PropTypes.string),
  loadedSession: PropTypes.object,
  loadedSubImages: PropTypes.array,
  updateSessionsCount: PropTypes.func,
  userInfo: PropTypes.object, // Assicurati che userInfo sia passato correttamente
};

export default ImageSelector;